import React from "react";
import { Database, Globe, Pause, Pencil, Play, Trash2 } from "lucide-react";

import { Badge } from "@/components/primitives/Badge";
import { Button } from "@/components/primitives/Button";
import { Card } from "@/components/primitives/Card";
import { Flow } from "@/types";

interface FlowCardProps {
  flow: Flow;
  onEdit: (flow: Flow) => void;
  onDelete: (id: string) => void;
  onToggle: (id: string) => void;
}

export const FlowCard: React.FC<FlowCardProps> = ({
  flow,
  onEdit,
  onDelete,
  onToggle,
}) => {
  const activeSteps = flow.steps.filter((s) => s.is_active).length;
  const latestStatus = flow.steps[0]?.runs?.[0]?.status ?? null;

  const statusVariant = !flow.is_active
    ? "neutral"
    : latestStatus === "success"
      ? "success"
      : latestStatus === "failure"
        ? "danger"
        : "neutral";

  const statusLabel = !flow.is_active ? "Paused" : latestStatus ?? "No runs";

  return (
    <Card className="flex flex-col p-0">
      <div className="p-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{flow.name}</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {flow.flow_type === "softnu_checks" ? `${Math.max(0, flow.steps.length - 1)} discovered service${flow.steps.length === 2 ? "" : "s"} · health check` : `${flow.steps.length} step${flow.steps.length !== 1 ? "s" : ""} · ${activeSteps} active`}
            </p>
          </div>
          <Badge variant={statusVariant}>{statusLabel}</Badge>
        </div>

        <div className="space-y-2">
          {flow.steps.slice(0, 3).map((step) => (
            <div
              key={step.id}
              className="flex items-center gap-3 rounded-xl bg-slate-50 px-3 py-2 text-sm dark:bg-slate-800/50"
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-white text-slate-500 shadow-sm dark:bg-slate-700 dark:text-slate-300">
                {step.step_type === "database" ? (
                  <Database className="h-3.5 w-3.5" />
                ) : (
                  <Globe className="h-3.5 w-3.5" />
                )}
              </span>
              <span className="truncate font-medium text-slate-700 dark:text-slate-200">
                {step.name}
              </span>
              <span className="ml-auto shrink-0 text-xs text-slate-400 dark:text-slate-500">
                {step.step_type === "imported_event" ? step.event_type ?? "Event" : `${step.period_minutes}m`}
              </span>
            </div>
          ))}
          {flow.steps.length > 3 && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              +{flow.steps.length - 3} more step{flow.steps.length - 3 !== 1 ? "s" : ""}
            </p>
          )}
        </div>
      </div>

      <div className="mt-auto flex items-center justify-between border-t border-slate-100 px-6 py-4 dark:border-slate-800">
        <Button
          variant={flow.is_active ? "secondary" : "primary"}
          size="sm"
          onClick={() => onToggle(flow.id)}
          className="gap-1.5"
        >
          {flow.is_active ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
          {flow.is_active ? "Pause" : "Unpause"}
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => onEdit(flow)} className="gap-1.5">
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete(flow.id)} className="gap-1.5 text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/30">
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </Button>
        </div>
      </div>
    </Card>
  );
};
